import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Loader2,
  FileText,
  Image as ImageIcon,
  Download,
  X,
  AlertCircle,
  Calendar,
  Clock,
  Target,
  RefreshCw,
  Rocket,
  Flag,
  Play,
  Presentation,
  History,
  Eye,
} from 'lucide-react';
import { PREDEFINED_DATE_TYPES, WORK_ITEM_STATUSES } from '@yoda/shared';
import type { WorkItemStatus } from '@yoda/shared';
import type { WorkItemDate } from '@yoda/shared';
import { CommentInput } from '../../components/comments/CommentInput';
import { AudioPlayer } from '../../components/comments/AudioPlayer';
import { cn, parseUTCDate, formatDate } from '../../lib/utils';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface WorkItem {
  id: string;
  title: string;
  description: string | null;
  type: string;
  status: string;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  projectId: string;
  projectName: string;
  dates?: WorkItemDate[];
  bundleId?: string | null;
  bundle?: { id: string; title: string } | null;
}

interface WorkItemVersion {
  id: string;
  workItemId: string;
  versionNumber: number;
  fileUrl: string;
  fileKey: string;
  thumbnailUrl: string | null;
  fileSize: number | null;
  mimeType: string | null;
  notes: string | null;
  uploadedBy: string;
  uploadedAt: string;
  uploader?: { id: string; name: string };
}

const dateTypeIcons: Record<string, typeof Calendar> = {
  deadline: AlertCircle,
  due_date: Calendar,
  next_update: RefreshCw,
  expected_completion: Target,
  estimated_completion: Clock,
  review_date: CheckCircle2,
  feedback_due: MessageSquare,
  go_live_date: Rocket,
  milestone_date: Flag,
  start_date: Play,
  presentation_date: Presentation,
};

interface CommentAttachment {
  id: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

interface Comment {
  id: string;
  content: string | null;
  voiceUrl: string | null;
  voiceDuration: number | null;
  attachments?: CommentAttachment[];
  createdAt: string;
  user: {
    name: string;
    isAdmin: boolean;
  };
}

export function WorkItemPage() {
  const { id } = useParams();
  const [workItem, setWorkItem] = useState<WorkItem | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRequestingChanges, setIsRequestingChanges] = useState(false);
  const [showApprovalSuccess, setShowApprovalSuccess] = useState(false);
  const [showRequestChangesModal, setShowRequestChangesModal] = useState(false);
  const [requestChangesComment, setRequestChangesComment] = useState('');
  const [versions, setVersions] = useState<WorkItemVersion[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<WorkItemVersion | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const [workItemRes, commentsRes, versionsRes] = await Promise.all([
          api.get<WorkItem>(`/work-items/${id}`),
          api.get<Comment[]>(`/work-items/${id}/comments`),
          api.get<WorkItemVersion[]>(`/work-items/${id}/versions`),
        ]);
        setWorkItem(workItemRes.data);
        setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);
        setVersions(Array.isArray(versionsRes.data) ? versionsRes.data : []);
        setError(null);
      } catch (err) {
        setError('Failed to load work item');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    if (id) fetchData();
  }, [id]);

  const handleApprove = async () => {
    if (!workItem) return;
    setIsApproving(true);

    try {
      await api.post(`/work-items/${workItem.id}/approve`);

      // Trigger confetti celebration!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b'],
      });

      setWorkItem({ ...workItem, status: 'approved' });
      setShowApprovalSuccess(true);
      toast.success('Approved! Great work!');

      setTimeout(() => setShowApprovalSuccess(false), 3000);
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve');
    } finally {
      setIsApproving(false);
    }
  };

  const handleRequestChangesClick = () => {
    setShowRequestChangesModal(true);
    setRequestChangesComment('');
  };

  const handleSubmitRequestChanges = async () => {
    if (!workItem || !requestChangesComment.trim()) return;
    setIsRequestingChanges(true);

    try {
      const response = await api.post<{ id: string; status: string; comment?: Comment }>(`/work-items/${workItem.id}/request-changes`, {
        comment: requestChangesComment.trim(),
      });

      setWorkItem({ ...workItem, status: 'changes_requested' });

      // Add the comment to the comments list if it was returned
      if (response.data.comment) {
        setComments([...comments, response.data.comment]);
      }

      setShowRequestChangesModal(false);
      setRequestChangesComment('');
      toast.success('Changes requested successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to request changes');
    } finally {
      setIsRequestingChanges(false);
    }
  };

  const handleComment = async (
    content: string | null,
    voiceBlob?: Blob,
    voiceDuration?: number,
    files?: File[]
  ) => {
    if (!workItem) return;

    try {
      // Use FormData for file uploads
      const formData = new FormData();

      if (content) {
        formData.append('content', content);
      }

      if (voiceBlob) {
        formData.append('voice', voiceBlob, 'voice.webm');
        if (voiceDuration) {
          formData.append('voiceDuration', voiceDuration.toString());
        }
      }

      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      }

      const response = await api.postFormData<Comment>(
        `/work-items/${workItem.id}/comments`,
        formData
      );

      setComments([...comments, response.data]);
      toast.success('Comment added!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to add comment');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error || !workItem) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Work item not found'}</p>
        <Link to="/" className="text-accent hover:underline">
          Back to Projects
        </Link>
      </div>
    );
  }

  const isApproved = workItem.status === 'approved';
  const canApprove = workItem.status === 'approval_requested';

  // Format date for display (relative time)
  const formatRelativeDate = (dateString: string) => {
    const date = parseUTCDate(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Link
          to={`/projects/${workItem.projectId}`}
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {workItem.projectName}
        </Link>

        <div className="flex flex-col gap-4">
          {/* Title row with status badge */}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold">{workItem.title}</h1>
            {(() => {
              const statusConfig = WORK_ITEM_STATUSES[workItem.status as WorkItemStatus];
              if (!statusConfig) return null;
              return (
                <span
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: statusConfig.bgColor,
                    color: statusConfig.color,
                  }}
                >
                  {statusConfig.label}
                </span>
              );
            })()}
          </div>

          {/* Description */}
          {workItem.description && (
            <p className="text-text-secondary max-w-2xl">
              {workItem.description}
            </p>
          )}

          {/* Approval buttons */}
          {!isApproved && canApprove && (
            <div className="flex flex-wrap gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleRequestChangesClick}
                disabled={isRequestingChanges}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border hover:border-red-500/50 hover:bg-red-500/5 text-text-secondary hover:text-red-500 transition-all disabled:opacity-50"
              >
                <XCircle className="w-5 h-5" />
                Request Changes
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleApprove}
                disabled={isApproving}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium shadow-lg shadow-green-500/25 disabled:opacity-50"
              >
                {isApproving ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Approve
                  </>
                )}
              </motion.button>
            </div>
          )}

          {isApproved && (
            <div className="flex flex-wrap items-center gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleRequestChangesClick}
                disabled={isRequestingChanges}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border hover:border-orange-500/50 hover:bg-orange-500/5 text-text-secondary hover:text-orange-500 transition-all disabled:opacity-50"
              >
                <AlertCircle className="w-5 h-5" />
                Request Further Changes
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Main content - fluid width with max for ultrawide */}
      <div className="space-y-6 max-w-[1800px] mx-auto">
        {/* Image/Video viewer with version info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {/* Preview - takes 2 columns */}
            <div className="lg:col-span-2 relative rounded-2xl overflow-hidden bg-bg-secondary border border-border flex items-center justify-center" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              {workItem.fileUrl ? (
                workItem.type === 'video' ? (
                  <video
                    src={workItem.fileUrl}
                    controls
                    className="w-full h-full object-contain"
                    style={{ maxHeight: 'calc(100vh - 200px)' }}
                  />
                ) : (
                  <motion.img
                    src={workItem.fileUrl}
                    alt={workItem.title}
                    className="w-full h-full object-contain"
                    style={{ maxHeight: 'calc(100vh - 200px)' }}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  />
                )
              ) : (
                <div className="aspect-video w-full flex items-center justify-center text-text-muted">
                  No preview available
                </div>
              )}

              {/* Approval success overlay */}
              <AnimatePresence>
                {showApprovalSuccess && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-green-500/20 flex items-center justify-center"
                  >
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 200 }}
                      className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center shadow-2xl"
                    >
                      <CheckCircle2 className="w-12 h-12 text-white" />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Version info sidebar */}
            <div className="bg-bg-secondary rounded-2xl border border-border p-5 flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-violet-500/10">
                  <History className="w-5 h-5 text-violet-500" />
                </div>
                <div>
                  <h3 className="font-semibold">
                    {versions.length > 0 ? `Version ${versions[0].versionNumber}` : 'Current Version'}
                  </h3>
                  {versions.length > 0 && versions[0].uploader && (
                    <p className="text-sm text-text-muted">
                      by {versions[0].uploader.name}
                    </p>
                  )}
                </div>
              </div>

              {/* Version notes/description - scrollable */}
              {versions.length > 0 && versions[0].notes ? (
                <div className="mb-4 min-h-0 flex-shrink overflow-y-auto">
                  <h4 className="text-sm font-medium text-text-muted mb-2">Update Notes</h4>
                  <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-wrap">
                    {versions[0].notes}
                  </p>
                </div>
              ) : (
                <div className="mb-4">
                  <p className="text-text-muted text-sm italic">No update notes for this version</p>
                </div>
              )}

              {/* Upload date */}
              {versions.length > 0 && (
                <div className="text-sm text-text-muted mb-4 mt-auto">
                  <span>Uploaded </span>
                  {formatDate(versions[0].uploadedAt, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </div>
              )}

              {/* Download button */}
              {workItem.fileUrl && (
                <a
                  href={workItem.fileUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Download Latest
                </a>
              )}

              {/* Version count */}
              {versions.length > 1 && (
                <button
                  onClick={() => setShowVersions(!showVersions)}
                  className="mt-3 text-sm text-accent hover:underline text-center"
                >
                  View all {versions.length} versions
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Key Dates section */}
        {workItem.dates && workItem.dates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="bg-bg-secondary rounded-2xl border border-border overflow-hidden">
              <div className="p-5 border-b border-border">
                <h2 className="text-lg font-semibold flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-accent/10">
                    <Calendar className="w-5 h-5 text-accent" />
                  </div>
                  Key Dates
                </h2>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {workItem.dates.map((date) => {
                    const Icon = date.dateTypeId && dateTypeIcons[date.dateTypeId]
                      ? dateTypeIcons[date.dateTypeId]
                      : Calendar;
                    const color = date.dateTypeId && PREDEFINED_DATE_TYPES[date.dateTypeId as keyof typeof PREDEFINED_DATE_TYPES]
                      ? PREDEFINED_DATE_TYPES[date.dateTypeId as keyof typeof PREDEFINED_DATE_TYPES].color
                      : '#6366f1';
                    const dateObj = parseUTCDate(date.dateValue);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isOverdue = dateObj < today;
                    const formattedDate = formatDate(date.dateValue, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    });

                    return (
                      <div
                        key={date.id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-bg-tertiary"
                      >
                        <div
                          className="p-2 rounded-lg flex-shrink-0"
                          style={{ backgroundColor: `${color}20` }}
                        >
                          <Icon className="w-4 h-4" style={{ color }} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{date.label}</p>
                          <p
                            className={cn(
                              'text-sm',
                              isOverdue ? 'text-red-500 font-medium' : 'text-text-muted'
                            )}
                          >
                            {formattedDate}
                            {isOverdue && ' (Overdue)'}
                          </p>
                          {date.notes && (
                            <p className="text-xs text-text-muted mt-0.5 truncate">{date.notes}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Version History - read-only for clients */}
        {versions.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="bg-bg-secondary rounded-2xl border border-border overflow-hidden">
              <button
                onClick={() => setShowVersions(!showVersions)}
                className="w-full p-5 flex items-center justify-between hover:bg-bg-tertiary/50 transition-colors"
              >
                <h2 className="text-lg font-semibold flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-violet-500/10">
                    <History className="w-5 h-5 text-violet-500" />
                  </div>
                  Version History
                  <span className="px-2.5 py-0.5 rounded-full bg-violet-500/10 text-violet-500 text-sm font-medium">
                    {versions.length}
                  </span>
                </h2>
                <motion.div
                  animate={{ rotate: showVersions ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowLeft className="w-5 h-5 text-text-muted -rotate-90" />
                </motion.div>
              </button>

              <AnimatePresence>
                {showVersions && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="p-5 pt-0 space-y-2">
                      {versions.map((version, index) => {
                        const isLatest = index === 0;
                        return (
                          <div
                            key={version.id}
                            className={cn(
                              'flex items-center gap-4 p-3 rounded-xl transition-colors',
                              isLatest ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-bg-tertiary'
                            )}
                          >
                            <div className={cn(
                              'w-10 h-10 rounded-lg flex items-center justify-center font-semibold text-sm',
                              isLatest ? 'bg-violet-500 text-white' : 'bg-bg-secondary text-text-muted'
                            )}>
                              v{version.versionNumber}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">
                                  {version.uploader?.name || 'Unknown'}
                                </span>
                                {isLatest && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400">
                                    Current
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-text-muted">
                                {formatDate(version.uploadedAt, {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                              {version.notes && (
                                <p className="text-xs text-text-secondary mt-1 truncate">
                                  {version.notes}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => setPreviewVersion(version)}
                              className="p-2 rounded-lg hover:bg-bg-secondary text-text-muted hover:text-accent transition-colors"
                              title="Preview this version"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* Bundle link */}
        {workItem.bundle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
          >
            <Link
              to={`/bundles/${workItem.bundle.id}`}
              className="block bg-violet-500/10 border border-violet-500/20 rounded-2xl p-5 hover:bg-violet-500/15 transition-colors"
            >
              <p className="text-sm font-medium text-violet-400 mb-1">Part of Bundle</p>
              <p className="font-semibold text-lg">{workItem.bundle.title}</p>
            </Link>
          </motion.div>
        )}

        {/* Comments section - now below the preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="bg-bg-secondary rounded-2xl border border-border overflow-hidden">
            {/* Comments header */}
            <div className="p-5 border-b border-border">
              <h2 className="text-lg font-semibold flex items-center gap-3">
                <div className="p-2 rounded-xl bg-accent/10">
                  <MessageSquare className="w-5 h-5 text-accent" />
                </div>
                Feedback & Comments
                {comments.length > 0 && (
                  <span className="px-2.5 py-0.5 rounded-full bg-accent/10 text-accent text-sm font-medium">
                    {comments.length}
                  </span>
                )}
              </h2>
              <p className="text-sm text-text-muted mt-1">
                Share your thoughts via text, voice message, or file attachment
              </p>
            </div>

            {/* Comment input - moved to top for better UX, hidden when approved */}
            {!isApproved && (
              <div className="p-5 border-b border-border bg-bg-tertiary/30">
                <CommentInput onSubmit={handleComment} projectId={workItem.projectId} />
              </div>
            )}

            {/* Read-only notice when approved */}
            {isApproved && (
              <div className="p-4 border-b border-border bg-green-500/5">
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">This item has been approved. Comments are now read-only.</span>
                </div>
              </div>
            )}

            {/* Comments list */}
            <div className="p-5">
              {comments.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-bg-tertiary flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-text-muted" />
                  </div>
                  <p className="text-text-muted font-medium">No comments yet</p>
                  <p className="text-sm text-text-muted mt-1">
                    Be the first to share your feedback!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment, index) => (
                    <motion.div
                      key={comment.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      className={cn(
                        'rounded-xl p-4',
                        comment.user.isAdmin
                          ? 'bg-bg-tertiary'
                          : 'bg-accent/5 border border-accent/20'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0',
                            comment.user.isAdmin
                              ? 'bg-accent text-white'
                              : 'bg-green-500 text-white'
                          )}
                        >
                          {comment.user.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">
                              {comment.user.name}
                            </span>
                            {comment.user.isAdmin && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-accent/10 text-accent">
                                Team
                              </span>
                            )}
                            <span className="text-xs text-text-muted ml-auto">
                              {formatDate(comment.createdAt)}
                            </span>
                          </div>

                          {comment.content && (
                            <p className="text-text-secondary leading-relaxed">
                              {comment.content}
                            </p>
                          )}

                          {comment.voiceUrl && (
                            <div className="mt-2">
                              <AudioPlayer
                                src={comment.voiceUrl}
                                duration={comment.voiceDuration || undefined}
                              />
                            </div>
                          )}

                          {/* File attachments */}
                          {comment.attachments && comment.attachments.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {comment.attachments.map((attachment) => {
                                const isImage = attachment.mimeType.startsWith('image/');
                                return (
                                  <a
                                    key={attachment.id}
                                    href={attachment.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group relative"
                                  >
                                    {isImage ? (
                                      <div className="w-24 h-24 rounded-lg overflow-hidden border border-border bg-bg-tertiary hover:border-accent/50 transition-colors">
                                        <img
                                          src={attachment.fileUrl}
                                          alt={attachment.fileName}
                                          className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                          <Download className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bg-tertiary hover:border-accent/50 transition-colors">
                                        <FileText className="w-4 h-4 text-text-muted" />
                                        <span className="text-sm text-text-secondary truncate max-w-[120px]">
                                          {attachment.fileName}
                                        </span>
                                        <Download className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                                      </div>
                                    )}
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Request Changes Modal */}
      <AnimatePresence>
        {showRequestChangesModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowRequestChangesModal(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            >
              <div className="bg-bg-secondary rounded-2xl border border-border shadow-2xl w-full max-w-lg pointer-events-auto overflow-hidden">
                {/* Header */}
                <div className="relative px-6 py-4 border-b border-border bg-gradient-to-r from-orange-500/10 to-red-500/10">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <AlertCircle className="w-6 h-6 text-orange-500" />
                    {isApproved ? 'Request Further Changes' : 'Request Changes'}
                  </h2>
                  <p className="text-sm text-text-muted mt-1">
                    Please describe what changes are needed
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowRequestChangesModal(false)}
                    className="absolute top-4 right-4 p-2 rounded-xl hover:bg-bg-tertiary transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                </div>

                {/* Content */}
                <div className="p-6">
                  <label className="block text-sm font-medium mb-2">
                    What changes would you like?
                  </label>
                  <textarea
                    value={requestChangesComment}
                    onChange={(e) => setRequestChangesComment(e.target.value)}
                    placeholder="Please describe the changes you need..."
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-bg-tertiary border border-border focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all outline-none resize-none"
                    autoFocus
                  />

                  <div className="flex gap-3 mt-6">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowRequestChangesModal(false)}
                      className="flex-1 px-4 py-3 rounded-xl border border-border hover:bg-bg-tertiary transition-colors"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSubmitRequestChanges}
                      disabled={isRequestingChanges || !requestChangesComment.trim()}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold shadow-lg shadow-orange-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                    >
                      {isRequestingChanges ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <XCircle className="w-5 h-5" />
                          Submit Request
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Version Preview Modal */}
      <AnimatePresence>
        {previewVersion && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setPreviewVersion(null)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-4 md:inset-8 z-50 flex flex-col bg-bg-secondary rounded-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500 text-white flex items-center justify-center font-semibold text-sm">
                    v{previewVersion.versionNumber}
                  </div>
                  <div>
                    <h3 className="font-semibold">Version {previewVersion.versionNumber}</h3>
                    <p className="text-sm text-text-muted">
                      {formatDate(previewVersion.uploadedAt, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {previewVersion.uploader && ` by ${previewVersion.uploader.name}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={previewVersion.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-accent transition-colors"
                    title="Download"
                  >
                    <Download className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => setPreviewVersion(null)}
                    className="p-2 rounded-lg hover:bg-bg-tertiary transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-bg-tertiary">
                {previewVersion.mimeType?.startsWith('video/') ? (
                  <video
                    src={previewVersion.fileUrl}
                    controls
                    className="max-w-full max-h-full rounded-lg"
                  />
                ) : (
                  <img
                    src={previewVersion.fileUrl}
                    alt={`Version ${previewVersion.versionNumber}`}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                )}
              </div>
              {previewVersion.notes && (
                <div className="p-4 border-t border-border bg-bg-secondary">
                  <p className="text-sm text-text-secondary">{previewVersion.notes}</p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
